import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const tokenRef = useRef("");

  const axiosAuth = useMemo(() => {
    const instance = axios.create({
      baseURL: API,
      withCredentials: true,
    });
    instance.interceptors.request.use((config) => {
      if (tokenRef.current) {
        config.headers.Authorization = `Bearer ${tokenRef.current}`;
      }
      return config;
    });
    return instance;
  }, []);

  const checkAuth = useCallback(async () => {
    if (!tokenRef.current) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axiosAuth.get("/auth/me");
      setUser(data);
    } catch (err) {
      console.error("Auth check failed:", err.message);
      setUser(null);
      tokenRef.current = "";
    } finally {
      setLoading(false);
    }
  }, [axiosAuth]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    tokenRef.current = data.token;
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axiosAuth.post("/auth/logout");
    } catch (err) {
      console.error("Logout request failed:", err.message);
    }
    setUser(null);
    tokenRef.current = "";
  }, [axiosAuth]);

  const value = useMemo(
    () => ({ user, loading, login, logout, axiosAuth }),
    [user, loading, login, logout, axiosAuth]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
